import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "@/store";
import { setRole, signOut } from "@/store/slices/auth";
import type { Role } from "@/types/domain";
import type { AuthUser } from "@/store/slices/auth";

/**
 * Thin Redux-backed replacement for the old context AuthProvider.
 * Same surface area as Clerk's `useUser()` so the swap-in remains one file.
 */
export function useAuth() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);

  const _signOut = useCallback(() => {
    dispatch(signOut());
    if (typeof window !== "undefined") window.location.assign("/sign-in");
  }, [dispatch]);

  const _setRole = useCallback(
    (role: Role) => dispatch(setRole(role)),
    [dispatch],
  );

  return { user, isLoading: false, signOut: _signOut, setRole: _setRole };
}

export function useCurrentRole(): Role | null {
  return useAppSelector((s) => s.auth.user?.role ?? null);
}

export type { AuthUser };
